import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { createPortal } from 'react-dom'

interface SearchBarProps {
  className?: string
  onClose?: () => void
  autoFocus?: boolean
}

interface TrendingItem {
  query: string
  delta?: 'up' | 'hot' | null
}

const RECENT_SEARCHES_KEY = 'ot_recent_searches'
const MAX_RECENT = 6

const TRENDING: TrendingItem[] = [
  { query: 'MacBook Pro M3',       delta: 'hot' },
  { query: 'RTX 4070',             delta: 'up'  },
  { query: 'Mechanical keyboard',  delta: null  },
  { query: 'USB-C hub',            delta: 'up'  },
  { query: 'Gaming monitor 144hz', delta: null  },
  { query: 'DDR5 RAM',             delta: 'hot' },
  { query: 'Webcam 4K',            delta: null  },
  { query: 'Laptop stand',         delta: null  },
]

function getRecentSearches(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) ?? '[]') }
  catch { return [] }
}
function saveRecentSearch(query: string) {
  const current = getRecentSearches().filter(q => q !== query)
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify([query, ...current].slice(0, MAX_RECENT)))
}
function removeRecentSearch(query: string) {
  localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(getRecentSearches().filter(q => q !== query)))
}

export const SearchBar: React.FC<SearchBarProps> = ({ className, onClose, autoFocus }) => {
  const [query, setQuery]           = useState('')
  const [focused, setFocused]       = useState(false)
  const [recentSearches, setRecent] = useState<string[]>([])
  const [panelPos, setPanelPos]     = useState<{ top: number; left: number; width: number } | null>(null)
  const [isMobile, setIsMobile]     = useState(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef     = useRef<HTMLInputElement>(null)
  const navigate     = useNavigate()

  const showPanel = focused && query.trim() === ''

  // Detect mobile viewport
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  const updatePos = useCallback(() => {
    if (containerRef.current) {
      const r = containerRef.current.getBoundingClientRect()
      setPanelPos({ top: r.bottom + 6, left: r.left, width: r.width })
    }
  }, [])

  useEffect(() => {
    if (showPanel) { setRecent(getRecentSearches()); updatePos() }
  }, [showPanel, updatePos])

  useEffect(() => {
    if (!showPanel) return
    window.addEventListener('scroll', updatePos, { passive: true })
    window.addEventListener('resize', updatePos)
    return () => { window.removeEventListener('scroll', updatePos); window.removeEventListener('resize', updatePos) }
  }, [showPanel, updatePos])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Element
      if (containerRef.current && !containerRef.current.contains(t) && !t.closest?.('[data-search-panel]'))
        setFocused(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setFocused(false); inputRef.current?.blur() }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    const q = query.trim()
    if (!q) return
    saveRecentSearch(q)
    setFocused(false)
    onClose?.()
    navigate(`/search?q=${encodeURIComponent(q)}`)
  }, [query, navigate, onClose])

  const handlePick = useCallback((q: string) => {
    saveRecentSearch(q)
    setFocused(false)
    setQuery('')
    onClose?.()
    navigate(`/search?q=${encodeURIComponent(q)}`)
  }, [navigate, onClose])

  const handleRemove = useCallback((q: string) => {
    removeRecentSearch(q)
    setRecent(getRecentSearches())
  }, [])

  return (
    <div ref={containerRef} className={className}>
      <form onSubmit={handleSubmit} role="search">
        <div className="relative flex items-center">

          {/* Search icon */}
          <div className="absolute left-3 sm:left-3.5 pointer-events-none" aria-hidden="true">
            <svg className="w-4 h-4 text-charcoal-light" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </div>

          {/* Input */}
          <input
            ref={inputRef}
            type="search"
            value={query}
            autoFocus={autoFocus}
            onChange={e => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            placeholder="Search products, brands…"
            autoComplete="off"
            aria-label="Search"
            className={`
              w-full h-10 pl-9 sm:pl-10 pr-14 sm:pr-16
              font-sans text-sm text-charcoal
              bg-royal-ivory border border-border-color rounded-royal
              placeholder:text-charcoal-light
              transition-all duration-150
              focus:outline-none focus:bg-white focus:border-royal-orange focus:ring-2 focus:ring-royal-orange/20
            `}
          />

          {/* Clear */}
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); inputRef.current?.focus() }}
              aria-label="Clear search"
              className="absolute right-12 sm:right-12 flex items-center justify-center w-5 h-5 rounded-full text-charcoal-light hover:text-charcoal hover:bg-border-color transition-all duration-150"
            >
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}

          {/* Submit — orange pill */}
          <button
            type="submit"
            aria-label="Submit search"
            className="absolute right-1 h-8 px-2.5 sm:px-3 rounded-royal bg-royal-orange hover:bg-royal-orange-dark text-white flex items-center justify-center transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-royal-orange focus-visible:ring-offset-1"
          >
            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </div>
      </form>

      {/* Suggestions panel */}
      {showPanel && panelPos && createPortal(
        <SearchPanel
          recentSearches={recentSearches}
          trending={TRENDING}
          onPick={handlePick}
          onRemoveRecent={handleRemove}
          pos={panelPos}
          isMobile={isMobile}
        />,
        document.body,
      )}
    </div>
  )
}

// ─── SearchPanel ──────────────────────────────────────────────────────────────

interface SearchPanelProps {
  recentSearches: string[]
  trending: TrendingItem[]
  onPick: (q: string) => void
  onRemoveRecent: (q: string) => void
  pos: { top: number; left: number; width: number }
  isMobile: boolean
}

const SearchPanel: React.FC<SearchPanelProps> = ({ recentSearches, trending, onPick, onRemoveRecent, pos, isMobile }) => {
  const hasRecent = recentSearches.length > 0

  // On mobile: full-width panel anchored to screen edges
  const panelStyle: React.CSSProperties = isMobile
    ? { position: 'fixed', top: pos.top, left: 8, right: 8, width: 'auto', zIndex: 9999 }
    : { position: 'fixed', top: pos.top, left: pos.left, width: Math.max(pos.width, 540), zIndex: 9999 }

  return (
    <div
      data-search-panel
      role="dialog"
      aria-label="Search suggestions"
      style={panelStyle}
      className="bg-white border border-border-color rounded-royal shadow-card-hover overflow-hidden animate-fade-in"
    >
      {/* Orange top accent */}
      <div className="h-0.5 bg-royal-orange" aria-hidden="true" />

      <div className="p-3 sm:p-4">

        {/* Mobile: stacked layout — Recent first, then Trending */}
        {isMobile ? (
          <div className="flex flex-col gap-4">
            {hasRecent && (
              <div>
                <PanelLabel icon={
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                } label="Recent" />
                <ul className="mt-2 flex flex-col gap-0.5">
                  {recentSearches.map(q => (
                    <li key={q} className="group flex items-center rounded-royal hover:bg-royal-ivory transition-colors">
                      <button
                        type="button"
                        onClick={() => onPick(q)}
                        className="flex-1 flex items-center gap-2.5 px-2.5 py-2 text-left focus-visible:outline-none min-w-0"
                      >
                        <svg className="w-3 h-3 text-border-color shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <span className="font-sans text-sm text-charcoal truncate">{q}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveRecent(q)}
                        aria-label={`Remove "${q}"`}
                        className="opacity-60 mr-2 p-1 rounded-royal text-charcoal-light hover:text-charcoal hover:bg-border-color transition-all focus-visible:opacity-100 focus-visible:outline-none shrink-0"
                      >
                        <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <PanelLabel icon={
                <svg className="w-3 h-3 text-royal-orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
                </svg>
              } label="Trending now" />
              {/* Mobile: single column for trending */}
              <ul className="mt-2 flex flex-col gap-0.5">
                {trending.slice(0, 6).map(({ query: q, delta }) => (
                  <li key={q}>
                    <button
                      type="button"
                      onClick={() => onPick(q)}
                      className="w-full flex items-center gap-2 px-2.5 py-2 rounded-royal hover:bg-royal-ivory transition-colors text-left group focus-visible:outline-none focus-visible:bg-royal-ivory"
                    >
                      <TrendingBadge delta={delta} />
                      <span className="font-sans text-sm text-charcoal truncate group-hover:text-royal-orange transition-colors duration-150">
                        {q}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          /* Desktop: side-by-side layout */
          <div className={`flex gap-0 ${hasRecent ? 'divide-x divide-border-color' : ''}`}>
            {hasRecent && (
              <div className="w-52 shrink-0 pr-4">
                <PanelLabel icon={
                  <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                } label="Recent" />
                <ul className="mt-2 flex flex-col gap-0.5">
                  {recentSearches.map(q => (
                    <li key={q} className="group flex items-center rounded-royal hover:bg-royal-ivory transition-colors">
                      <button
                        type="button"
                        onClick={() => onPick(q)}
                        className="flex-1 flex items-center gap-2.5 px-2.5 py-2 text-left focus-visible:outline-none min-w-0"
                      >
                        <svg className="w-3 h-3 text-border-color shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <span className="font-sans text-sm text-charcoal truncate">{q}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveRecent(q)}
                        aria-label={`Remove "${q}"`}
                        className="opacity-0 group-hover:opacity-100 mr-2 p-1 rounded-royal text-charcoal-light hover:text-charcoal hover:bg-border-color transition-all focus-visible:opacity-100 focus-visible:outline-none shrink-0"
                      >
                        <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={`flex-1 ${hasRecent ? 'pl-4' : ''}`}>
              <PanelLabel icon={
                <svg className="w-3 h-3 text-royal-orange" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" />
                </svg>
              } label="Trending now" />
              <ul className="mt-2 grid grid-cols-2 gap-x-2 gap-y-0.5">
                {trending.map(({ query: q, delta }) => (
                  <li key={q}>
                    <button
                      type="button"
                      onClick={() => onPick(q)}
                      className="w-full flex items-center gap-2 px-2.5 py-2 rounded-royal hover:bg-royal-ivory transition-colors text-left group focus-visible:outline-none focus-visible:bg-royal-ivory"
                    >
                      <TrendingBadge delta={delta} />
                      <span className="font-sans text-sm text-charcoal truncate group-hover:text-royal-orange transition-colors duration-150">
                        {q}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Footer keyboard hints — desktop only */}
        {!isMobile && (
          <div
            className="mt-3 pt-3 flex items-center gap-1.5 px-1"
            style={{ borderTop: '1px solid var(--color-border-color, #E0E0E0)' }}
          >
            <Kbd>↵</Kbd>
            <span className="font-sans text-[10px] text-charcoal-light">to search</span>
            <span className="mx-1.5 text-border-color" aria-hidden="true">·</span>
            <Kbd>esc</Kbd>
            <span className="font-sans text-[10px] text-charcoal-light">to close</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TrendingBadge: React.FC<{ delta?: 'up' | 'hot' | null }> = ({ delta }) => {
  if (delta === 'hot') return (
    <span className="shrink-0 font-display text-[9px] font-extrabold text-white bg-royal-orange rounded px-1.5 py-0.5 leading-none uppercase tracking-wide">
      Hot
    </span>
  )
  if (delta === 'up') return (
    <svg className="w-3 h-3 text-success shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
      <polyline points="18 15 12 9 6 15" />
    </svg>
  )
  return (
    <svg className="w-3 h-3 text-border-color shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  )
}

const PanelLabel: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="flex items-center gap-1.5 px-2">
    <span className="text-charcoal-light">{icon}</span>
    <span className="font-display text-[10px] font-bold uppercase tracking-[0.15em] text-charcoal-light">
      {label}
    </span>
  </div>
)

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="font-sans text-[10px] text-charcoal-light bg-royal-ivory border border-border-color rounded px-1.5 py-0.5 leading-none">
    {children}
  </kbd>
)